// Dashboard.js
import React, { useState } from "react";
import StrategyCombinationRow from "./StrategyCombinationRow";
import "../App.css";

export default function Dashboard({
  timeframe,
  setTimeframe,
  selectedVault,
  setSelectedVault,
  selectedAave,
  setSelectedAave,
  parseVaultApy,
  parseAaveBorrowRate,
  handleFindBestNetApy,
  handlePrevBest,
  handleNextBest,
  bestNetApyIndex,
  bestNetApyResults,
  ltv,
  setLtv,
  netApyStr,
  leveragedApyStr,
  isScrolled,
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [tempValue, setTempValue] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const [isBorrowHovered, setIsBorrowHovered] = useState(false);

  const vaultSymbol = selectedVault?.token?.symbol || "N/A";
  const vaultProtocol = selectedVault?.protocol || selectedVault?.label || "";
  const aaveSymbol = selectedAave?.symbol || "";
  const [selectedStrategy, setSelectedStrategy] = useState(null);

  const isBothSelected = selectedVault && selectedAave;

  // PlaceholderRow component
  const PlaceholderRow = ({ type }) => {
    const assetLabel = type === "collateral" ? "Asset" : "Borrow";
    const yieldLabel = type === "collateral" ? "Yield" : "From";
    return (
      <div className="strategy-combination-row placeholder">
        <div className="strategy-label-row">
          <span>{assetLabel}</span>
          <span>{yieldLabel}</span>
        </div>
        <div className="strategy-combination-content">
          <div className="strategy-asset-section">
            <div className="strategy-asset-logo placeholder"></div>
            <span className="strategy-asset-name">Select</span>
          </div>
          <div className="strategy-arrow-icon"></div>
          <div className="strategy-yield-section">
            <div className="strategy-yield-logo placeholder"></div>
            <span className="strategy-yield-name">Select</span>
          </div>
          <div className="strategy-apy-box">-</div>
        </div>
      </div>
    );
  };

  // Event handlers
  const handleLocalChange = (e) => {
    setTempValue(e.target.value);
  };

  const handleFocus = () => {
    setIsFocused(true);
    setTempValue("");
  };

  const handleBlur = () => {
    setIsFocused(false);
    let val = parseFloat(tempValue);
    if (isNaN(val)) val = 90.0;
    if (val < 0) val = 0;
    if (val > 100) val = 100;
    setLtv(String(val.toFixed(1)));
  };

  const getInputDisplay = () => {
    if (isFocused) return tempValue;
    const numericVal = parseFloat(ltv);
    return !numericVal ? "90.0%" : `${ltv}%`;
  };

  const calculateLeverageRatio = () => {
    const ltvValue = parseFloat(ltv) / 100;
    if (ltvValue >= 1 || isNaN(ltvValue)) return "∞";
    return (1 / (1 - ltvValue)).toFixed(0);
  };

  return (
    <div
      className={`dashboard-wrapper ${isScrolled ? "dashboard-scrolled" : ""}`}
    >
      <div className="dashboard-header">
        <div className="dashboard-header-title">
          <span>CPL Dashboard</span>
        </div>
        <div className="dashboard-header-right">
          <span className="timeframe-label">Time Frame</span>
          <div className="custom-select-wrapper">
            <select
              value={timeframe || "30day"}
              onChange={(e) => setTimeframe(e.target.value)}
              className="select-dropdown"
            >
              <option value="30day">30day</option>
              <option value="7day">7day</option>
              <option value="1day">1day</option>
            </select>
            <img
              src="/downBtn.png"
              alt="down arrow"
              className="select-dropdown-arrow"
            />
          </div>
        </div>
      </div>

      <div className="dash-area">
        <div className="select-area">
          <div className="strategy-combination-area">
            <div className="strategy-combination-container">
              {selectedVault ? (
                <StrategyCombinationRow
                  type="collateral"
                  asset={vaultSymbol}
                  yieldSource={vaultProtocol}
                  apy={(parseVaultApy(selectedVault) * 100).toFixed(2)}
                />
              ) : (
                <PlaceholderRow type="collateral" />
              )}
              <div className="strategy-combination-divider" />
              {selectedAave ? (
                <StrategyCombinationRow
                  type="borrow"
                  asset={aaveSymbol}
                  yieldSource="aave"
                  apy={(parseAaveBorrowRate(selectedAave) * 100).toFixed(2)}
                />
              ) : (
                <PlaceholderRow type="borrow" />
              )}
            </div>
          </div>

          <div className="findbest-area">
            <div className="best-apy-nav">
              <div className="find-text">
                Find <span className="highlight">#{bestNetApyIndex + 1}</span>{" "}
                Net APY for
              </div>
              <div className="nav-controls">
                <button
                  onClick={handlePrevBest}
                  disabled={bestNetApyIndex === 0}
                >
                  <img src="/prevbtn.png" alt="Previous" />
                </button>
                <div className="result-number">#{bestNetApyIndex + 1}</div>
                <button
                  onClick={handleNextBest}
                  disabled={bestNetApyIndex >= bestNetApyResults.length - 1}
                >
                  <img src="/nextbtn.png" alt="Next" />
                </button>
              </div>
            </div>
            <div className="strategy-buttons">
              <button
                className={`strategy-button ${
                  selectedStrategy === "stable-stable" ? "selected" : ""
                }`}
                onClick={() => {
                  if (selectedStrategy === "stable-stable") {
                    setSelectedStrategy(null);
                    setSelectedVault(null);
                    setSelectedAave(null);
                  } else {
                    handleFindBestNetApy("stable-stable");
                    setSelectedStrategy("stable-stable");
                  }
                }}
              >
                Stable{"<>"}Stable
              </button>
              <button
                className={`strategy-button ${
                  selectedStrategy === "eth-eth" ? "selected" : ""
                }`}
                onClick={() => {
                  if (selectedStrategy === "eth-eth") {
                    setSelectedStrategy(null);
                    setSelectedVault(null);
                    setSelectedAave(null);
                  } else {
                    handleFindBestNetApy("eth-eth");
                    setSelectedStrategy("eth-eth");
                  }
                }}
              >
                ETH{"<>"}ETH
              </button>
              <button
                className={`strategy-button ${
                  selectedStrategy === "eth-long" ? "selected" : ""
                }`}
                onClick={() => {
                  if (selectedStrategy === "eth-long") {
                    setSelectedStrategy(null);
                    setSelectedVault(null);
                    setSelectedAave(null);
                  } else {
                    handleFindBestNetApy("eth-long");
                    setSelectedStrategy("eth-long");
                  }
                }}
              >
                ETH Long
              </button>
              <button
                className={`strategy-button ${
                  selectedStrategy === "eth-short" ? "selected" : ""
                }`}
                onClick={() => {
                  if (selectedStrategy === "eth-short") {
                    setSelectedStrategy(null);
                    setSelectedVault(null);
                    setSelectedAave(null);
                  } else {
                    handleFindBestNetApy("eth-short");
                    setSelectedStrategy("eth-short");
                  }
                }}
              >
                ETH Short
              </button>
            </div>
          </div>
        </div>
        <div className="results-container">
          <div className="result-area">
            {isBothSelected ? (
              <>
                <div className="apy-wrap">
                  <div className="apy-label-row">
                    <span className="apy-label">APY</span>
                    <span className="apy-label">Max ROE</span>
                  </div>
                  <div className="apy-display">
                    <div className="net-apy">{netApyStr || "-"}</div>
                    <div className="leveraged-apy">
                      {leveragedApyStr || "-"}
                    </div>
                  </div>
                </div>
                <div className="ltv-input">
                  <div className="ltv-label">LTV</div>
                  <div className="ltv-input-field">
                    <input
                      type="text"
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onChange={handleLocalChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.target.blur(); // Triggers onBlur to confirm input
                        }
                      }}
                      value={getInputDisplay()}
                    />
                  </div>
                  <div className="ratio-separator">/</div>
                  <div className="leverage-label">Leverage Ratio</div>
                  <div className="leverage-value">
                    {calculateLeverageRatio()}x
                  </div>
                </div>
              </>
            ) : (
              <div className="apy-wrap">
                <div className="apy-label-row">
                  <span className="apy-label">APY</span>
                  <span className="apy-label">Max ROE</span>
                </div>
                <div className="apy-display">
                  <span className="net-apy">-</span>
                  <span className="leveraged-apy">-</span>
                </div>
              </div>
            )}
          </div>
          <a
            href={
              isBothSelected
                ? "https://7wplm4b0ibg.typeform.com/to/GDGvVZmS"
                : undefined
            }
            target="_blank"
            rel="noopener noreferrer"
            className={`borrow-box ${!isBothSelected ? "unselected" : ""}`}
            onMouseEnter={() => isBothSelected && setIsBorrowHovered(true)}
            onMouseLeave={() => isBothSelected && setIsBorrowHovered(false)}
          >
            <span className="borrow-text">
              {isBothSelected && isBorrowHovered
                ? "✨ Join Exclusive Mainnet"
                : "Borrow"}
            </span>
          </a>
        </div>
      </div>
    </div>
  );
}
